import { forwardRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { READING } from "@/constants/testIds";

/**
 * ShareCardStory — 9:16 Instagram-Story-ready composition, 1080×1920.
 * Compact interpretation (truncated with ellipsis if too long).
 */
export const ShareCardStory = forwardRef(function ShareCardStory(
  { consultant, question, picks, interpretation, createdAt },
  ref
) {
  const { t, language } = useLanguage();

  const dateStr = createdAt
    ? new Date(createdAt).toLocaleDateString(language === "fr" ? "fr-FR" : "en-US", {
        year: "numeric", month: "long", day: "numeric",
      })
    : "";

  const byPos = Object.fromEntries((picks || []).map((p) => [p.position, p]));

  // Trim interpretation to ~700 chars for Story format
  const trimmed = (interpretation || "").length > 700
    ? interpretation.slice(0, 700).trimEnd() + "…"
    : interpretation || "";

  const positions = [
    { row: 0, col: 1, key: "top" },
    { row: 1, col: 0, key: "left" },
    { row: 1, col: 1, key: "center" },
    { row: 1, col: 2, key: "right" },
    { row: 2, col: 1, key: "bottom" },
  ];

  return (
    <div
      ref={ref}
      data-testid={READING.shareCardStory}
      style={{
        width: "1080px",
        height: "1920px",
        backgroundColor: "#F4EAD1",
        color: "#14103A",
        padding: "80px 72px 60px",
        fontFamily: "'Cormorant Garamond', serif",
        boxSizing: "border-box",
        border: "3px solid #8A5A0F",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{
        position: "absolute", inset: "18px",
        border: "1px solid rgba(138,90,15,0.4)", pointerEvents: "none",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center" }}>
        <div style={{
          letterSpacing: "0.65em", fontSize: "16px", color: "#8A5A0F",
          fontFamily: "'Outfit', sans-serif", fontWeight: 500, textTransform: "uppercase",
        }}>
          ✦ ARCANA MYSTIQUE ✦
        </div>
        <div style={{
          fontStyle: "italic", fontWeight: 300, fontSize: "68px",
          lineHeight: 1.05, marginTop: "24px", color: "#14103A",
        }}>
          {t.reading.phase.result}
        </div>
        <div style={{
          marginTop: "14px", letterSpacing: "0.4em", fontSize: "13px",
          color: "rgba(20,16,58,0.6)", fontFamily: "'Outfit', sans-serif", textTransform: "uppercase",
        }}>
          {dateStr}
        </div>
        <div style={{
          width: "260px", height: "1px", margin: "26px auto 0",
          background: "linear-gradient(90deg, transparent, rgba(138,90,15,0.8), transparent)",
        }} />
      </div>

      {/* Question */}
      <div style={{
        textAlign: "center", fontStyle: "italic", fontSize: "26px", lineHeight: 1.4,
        color: "rgba(20,16,58,0.85)", margin: "36px auto 40px", maxWidth: "880px",
      }}>
        {question ? `« ${question} »` : language === "fr" ? "— Sagesse du tarot —" : "— Wisdom of the tarot —"}
      </div>

      {/* Consultant */}
      <div style={{ textAlign: "center", marginBottom: "36px" }}>
        <div style={{
          letterSpacing: "0.4em", fontSize: "13px", color: "#8A5A0F",
          fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: "14px",
        }}>
          {t.history.consultantLabel}
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <MiniCard card={consultant} language={language} size="lg" />
        </div>
      </div>

      {/* Cross grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gridTemplateRows: "repeat(3, auto)",
        gap: "16px", justifyItems: "center", alignItems: "center", marginBottom: "32px",
      }}>
        {[0, 1, 2].flatMap((row) =>
          [0, 1, 2].map((col) => {
            const cell = positions.find((p) => p.row === row && p.col === col);
            if (!cell) return <div key={`${row}-${col}`} />;
            const p = byPos[cell.key];
            return (
              <div key={cell.key} style={{ textAlign: "center" }}>
                <div style={{
                  letterSpacing: "0.32em", fontSize: "11px", color: "#8A5A0F",
                  fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: "6px",
                }}>
                  {t.reading.posShort[cell.key]}
                </div>
                <MiniCard card={p?.card} language={language} size="sm" />
              </div>
            );
          })
        )}
      </div>

      <div style={{
        width: "100%", height: "1px",
        background: "linear-gradient(90deg, transparent, rgba(138,90,15,0.55), transparent)",
        marginBottom: "24px",
      }} />

      {/* Truncated interpretation */}
      <div style={{
        fontSize: "20px", lineHeight: 1.55, color: "#14103A",
        whiteSpace: "pre-wrap", fontFamily: "'Cormorant Garamond', serif", flex: 1,
      }}>
        {trimmed}
      </div>

      <div style={{
        textAlign: "center", marginTop: "20px", paddingTop: "16px",
        borderTop: "1px solid rgba(138,90,15,0.3)",
        letterSpacing: "0.36em", fontSize: "12px",
        color: "rgba(20,16,58,0.55)", fontFamily: "'Outfit', sans-serif", textTransform: "uppercase",
      }}>
        {t.footer}
      </div>
    </div>
  );
});

const MiniCard = ({ card, language, size = "sm" }) => {
  const dims = size === "lg"
    ? { w: 260, h: 400, name: 22 }
    : { w: 165, h: 258, name: 15 };
  return (
    <div style={{
      width: `${dims.w}px`, height: `${dims.h}px`, margin: "0 auto",
      background: "#F4EAD1", border: "1px solid #8A5A0F", borderRadius: "10px",
      overflow: "hidden", display: "flex", flexDirection: "column",
      boxShadow: "0 10px 24px -12px rgba(20,16,58,0.35)",
    }}>
      {card ? (
        <>
          <div style={{ flex: 1, background: "#F4EAD1", overflow: "hidden" }}>
            {card.image && (
              <img
                src={card.image}
                alt={language === "fr" ? card.name_fr : card.name_en}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            )}
          </div>
          <div style={{
            width: "100%", padding: "6px 8px", textAlign: "center",
            borderTop: "1px solid rgba(138,90,15,0.5)", background: "rgba(244,234,209,0.96)",
          }}>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: `${dims.name}px`, color: "#14103A", lineHeight: 1.15,
            }}>
              {language === "fr" ? card.name_fr : card.name_en}
            </div>
          </div>
        </>
      ) : (
        <div style={{ flex: 1, border: "1px dashed rgba(138,90,15,0.4)", borderRadius: "6px", margin: "8px" }} />
      )}
    </div>
  );
};

export default ShareCardStory;
